import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { type ApAgentAction } from "@/accounts-payable/types";
import { formatDollars } from "@/accounts-payable/utils";
import { cn } from "@/core/utils/utils";

/**
 * On a pending PO whose lines carry new pricebook items: lists the ones its
 * approval also creates, and shows the API's reason when it would refuse the
 * approval (e.g. one of the items was dismissed).
 */
export const NewPricebookItemsNote = ({
  items,
  blockedReason,
  blockedReasonId,
}: {
  /** The PO's pending `create_pricebook_item` prerequisites. */
  items: ApAgentAction[];
  /** The API's `approval_blocked_reason` for the PO. */
  blockedReason?: string | null;
  /** Id for the reason, which the disabled Approve button points to. */
  blockedReasonId: string;
}) => {
  const count = items.length;

  return (
    <Stack
      gap="sm"
      fullWidth
      className={cn("rounded-md border px-3 py-3", {
        "border-stroke-negative": blockedReason,
        "border-stroke-secondary": !blockedReason,
      })}
    >
      {count > 0 && (
        <>
          {/* A list label, not a sentence: the approve button already says
              approving the PO creates these, and counts the same items. */}
          <Typography variant="subtitleW3">
            New pricebook items ({count})
          </Typography>
          <Stack gap="xs" fullWidth>
            {items.map((item) => (
              <Stack
                key={item.id}
                direction="row"
                justify="between"
                gap="md"
                fullWidth
              >
                <Stack gap="none" className="min-w-0">
                  <Typography variant="bodyW2">{item.payload?.name}</Typography>
                  <Typography variant="captionW1" textColor="tertiary">
                    {item.payload?.code}
                  </Typography>
                </Stack>
                <Typography variant="bodyW2" className="flex-none tabular-nums">
                  {formatDollars(item.payload?.cost)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
      {blockedReason && (
        <Typography
          id={blockedReasonId}
          variant="captionW1"
          textColor="negative"
        >
          {blockedReason}
        </Typography>
      )}
    </Stack>
  );
};
